/**
 * "A practice run just ended — drop whatever it put on you."
 *
 * ── WHY EVERY SCREEN THAT WRITES NEEDS THIS ──────────────────────────────────────────────────────
 * Practice mode owns the transport and nothing else. It can un-patch fetch and XHR when a run ends;
 * it cannot un-say what its synthesised 201s told a React component twenty seconds earlier. So a run
 * ends and the screen keeps showing a PIN that was never issued, a sticker that was never minted, a
 * draft that was never saved, a sign-off that never reached anybody's pay — with every control beside
 * them going live at that instant, because practice mode has just been switched off.
 *
 * ── EVERY SCREEN A PRACTICE TOUR WRITES ON NEEDS ONE, AND THAT IS GUARDED ────────────────────────
 * This used to be a hand-maintained list of call sites with nothing holding it honest, and it was
 * already one short: `jsa-fill` — the newest tour, authored in another worktree — spends eight of its
 * fourteen steps on FormFill, which subscribed to nothing, and all 312 tour tests were green. The
 * guard in __tests__/tourWalkthrough.test.jsx now takes the SCREENS of every practice tour, mounts
 * the ones that write, and fails BY NAME when a mounted screen does not subscribe. A screen that
 * genuinely has nothing to put back is listed there with the reason typed out.
 *
 * ── WHAT A GOOD RESET DOES ───────────────────────────────────────────────────────────────────────
 * Prefer RE-READING FROM THE SERVER over clearing. Clearing replaces a fiction with a blank; a re-read
 * replaces it with the truth, which is what the reader needs in order to know what is actually there.
 * By the time this hook's callback runs, practice mode is already off (`disarmPractice` clears `armed`
 * and restores both transports before it broadcasts), so a request made from here is a real one.
 *
 * TWO MORE THINGS ARE TRUE BY THE TIME IT RUNS, and both are properties of disarmPractice rather than
 * of any screen — read its header before relying on either:
 *
 *   • THE RUN FABRICATED SOMETHING. A run that blocked no write never reaches here at all, so a
 *     callback may be blunt about state the run WOULD have produced without eating the real thing a
 *     person typed before pressing "Show me how".
 *   • EVERY ANSWER THIS RUN HANDED OUT HAS ALREADY LANDED. A blocked write is answered a macrotask
 *     after it is intercepted, while a `requested` gate fires on the interception — so the end of a
 *     tour can arrive before its own last 201 does. The broadcast waits it out. Without that, a
 *     callback ran and the success handler it was undoing ran AFTER it.
 *
 * ── AND WHAT IT MUST NOT DO ──────────────────────────────────────────────────────────────────────
 * It must not destroy real unsaved work. On a screen holding a foreman's typing, re-reading the whole
 * record would throw away a day of crew hours to undo a banner — so those screens take the NARROW
 * reset: drop the fabricated claims (and any id `isPracticeId` can prove is fictional), leave the
 * typing alone. Say which of the two a screen takes, and why, where you call this.
 *
 * The callback is held in a ref, so a plain inline arrow is fine — the subscription is made once on
 * mount and torn down on unmount, and never re-made when the callback's closure changes.
 */
import { useEffect, useRef } from 'react'

import { subscribePracticeEnded } from './practiceMode'

/** @param {() => void} onEnded */
export function usePracticeReset(onEnded) {
  const latest = useRef(onEnded)
  latest.current = onEnded
  useEffect(() => subscribePracticeEnded(() => { latest.current() }), [])
}

export default usePracticeReset
